import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { CsvRow } from "@/hooks/useDashboardData";
import CsvProductImporter from "./CsvProductImporter";

interface KnowledgeBasePanelProps {
  csvData: CsvRow[];
  loading: boolean;
  phone: string;
  onImport: (file: File, phone: string) => Promise<{ success: boolean; message?: string; data?: { rows: number; plan: string; expires_at: string } }>;
}

interface FormField {
  key: string;
  label: string;
  placeholder: string;
  section: "business" | "product";
  isTextarea?: boolean;
}

const BUSINESS_FIELDS_KEYS: FormField[] = [
  { key: "name", label: "Business Name", placeholder: "Company or business name", section: "business" },
  { key: "industry", label: "Industry", placeholder: "e.g. Software Development & Digital Solutions", section: "business" },
  { key: "description", label: "Description", placeholder: "Brief description of the business", section: "business", isTextarea: true },
  { key: "email", label: "Email", placeholder: "contact@example.com", section: "business" },
  { key: "website", label: "Website", placeholder: "https://example.com", section: "business" },
  { key: "working_hours", label: "Working Hours", placeholder: "e.g. Daily from 9 AM to 5 PM", section: "business" },
  { key: "address", label: "Address", placeholder: "Physical address", section: "business" },
  { key: "service_monthly", label: "Monthly Service", placeholder: "Description of monthly service offerings", section: "business", isTextarea: true },
  { key: "service_quarterly", label: "Quarterly Service", placeholder: "Description of quarterly service offerings", section: "business", isTextarea: true },
  { key: "service_trainer", label: "Training Service", placeholder: "Description of training offerings", section: "business", isTextarea: true },
];

const PRODUCT_FIELDS_KEYS: FormField[] = [
  { key: "product_name", label: "Product Name", placeholder: "e.g. Wireless Bluetooth Headphones Pro X200", section: "product" },
  { key: "product_description", label: "Description", placeholder: "Detailed description of the product", section: "product", isTextarea: true },
  { key: "category", label: "Category", placeholder: "e.g. Electronics, Fashion, Home & Garden", section: "product" },
  { key: "brand", label: "Brand", placeholder: "e.g. Apple, Samsung, Nike", section: "product" },
  { key: "price", label: "Price", placeholder: "e.g. 299.99", section: "product" },
  { key: "features", label: "Key Features", placeholder: "Main features and specifications separated by commas", section: "product", isTextarea: true },
  { key: "inventory_status", label: "Inventory Status", placeholder: "e.g. In Stock, Out of Stock, Pre-order", section: "product" },
  { key: "product_url", label: "Product URL", placeholder: "https://example.com/product", section: "product" },
];

const BUSINESS_FIELDS = BUSINESS_FIELDS_KEYS;
const PRODUCT_FIELDS = PRODUCT_FIELDS_KEYS;

function getBusinessFields(t: (k: string) => string): FormField[] {
  return [
    { key: "name", label: t("kb.field.name"), placeholder: t("kb.field.namePlaceholder"), section: "business" },
    { key: "industry", label: t("kb.field.industry"), placeholder: t("kb.field.industryPlaceholder"), section: "business" },
    { key: "description", label: t("kb.field.description"), placeholder: t("kb.field.descriptionPlaceholder"), section: "business", isTextarea: true },
    { key: "email", label: t("kb.field.email"), placeholder: t("kb.field.emailPlaceholder"), section: "business" },
    { key: "website", label: t("kb.field.website"), placeholder: t("kb.field.websitePlaceholder"), section: "business" },
    { key: "working_hours", label: t("kb.field.workingHours"), placeholder: t("kb.field.workingHoursPlaceholder"), section: "business" },
    { key: "address", label: t("kb.field.address"), placeholder: t("kb.field.addressPlaceholder"), section: "business" },
    { key: "service_monthly", label: t("kb.field.serviceMonthly"), placeholder: t("kb.field.serviceMonthlyPlaceholder"), section: "business", isTextarea: true },
    { key: "service_quarterly", label: t("kb.field.serviceQuarterly"), placeholder: t("kb.field.serviceQuarterlyPlaceholder"), section: "business", isTextarea: true },
    { key: "service_trainer", label: t("kb.field.serviceTrainer"), placeholder: t("kb.field.serviceTrainerPlaceholder"), section: "business", isTextarea: true },
  ];
}

function getProductFields(t: (k: string) => string): FormField[] {
  return [
    { key: "product_name", label: t("kb.field.productName"), placeholder: t("kb.field.productNamePlaceholder"), section: "product" },
    { key: "product_description", label: t("kb.field.productDescription"), placeholder: t("kb.field.productDescriptionPlaceholder"), section: "product", isTextarea: true },
    { key: "category", label: t("kb.field.category"), placeholder: t("kb.field.categoryPlaceholder"), section: "product" },
    { key: "brand", label: t("kb.field.brand"), placeholder: t("kb.field.brandPlaceholder"), section: "product" },
    { key: "price", label: t("kb.field.price"), placeholder: t("kb.field.pricePlaceholder"), section: "product" },
    { key: "features", label: t("kb.field.features"), placeholder: t("kb.field.featuresPlaceholder"), section: "product", isTextarea: true },
    { key: "inventory_status", label: t("kb.field.inventoryStatus"), placeholder: t("kb.field.inventoryStatusPlaceholder"), section: "product" },
    { key: "product_url", label: t("kb.field.productUrl"), placeholder: t("kb.field.productUrlPlaceholder"), section: "product" },
  ];
}

interface ProductEntry {
  id: string;
  values: Record<string, string>;
}

function escapeCsvValue(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function emptyProductValues(): Record<string, string> {
  const initial: Record<string, string> = {};
  PRODUCT_FIELDS_KEYS.forEach((f) => {
    initial[f.key] = "";
  });
  return initial;
}

let productCounter = 0;
function nextProductId(): string {
  productCounter += 1;
  return `p_${Date.now()}_${productCounter}`;
}

function parseProductsFromCsv(csvData: CsvRow[]): ProductEntry[] {
  const fields = PRODUCT_FIELDS_KEYS;
  const productsMap = new Map<number, Record<string, string>>();

  csvData.forEach((row) => {
    const match = row.key.match(/^product_(\d+)_(.+)$/);
    if (match) {
      const idx = parseInt(match[1], 10);
      const fieldKey = match[2];
      if (!productsMap.has(idx)) {
        productsMap.set(idx, emptyProductValues());
      }
      const values = productsMap.get(idx)!;
      const allKeys = PRODUCT_FIELDS_KEYS.map(f => f.key);
      if (allKeys.includes(fieldKey)) {
        values[fieldKey] = row.value;
      }
    }
  });

  if (productsMap.size === 0) {
    const legacy: Record<string, string> = emptyProductValues();
    let hasLegacy = false;
    const legacyKeys = new Set(PRODUCT_FIELDS_KEYS.map(f => f.key));
    csvData.forEach((row) => {
      if (legacyKeys.has(row.key)) {
        legacy[row.key] = row.value;
        hasLegacy = true;
      }
    });
    if (hasLegacy) {
      return [{ id: nextProductId(), values: legacy }];
    }
    return [];
  }

  const sortedIndices = Array.from(productsMap.keys()).sort((a, b) => a - b);
  return sortedIndices.map((idx) => ({
    id: nextProductId(),
    values: productsMap.get(idx)!,
  }));
}

function buildKeyValueCsv(businessValues: Record<string, string>, products: ProductEntry[]): File {
  const lines: string[] = [];

  BUSINESS_FIELDS_KEYS.forEach((f) => {
    const val = businessValues[f.key]?.trim();
    if (val) {
      lines.push(`${escapeCsvValue(f.key)},${escapeCsvValue(val)}`);
    }
  });

  products.forEach((product, idx) => {
    PRODUCT_FIELDS_KEYS.forEach((f) => {
      const val = product.values[f.key]?.trim();
      if (val) {
        const indexedKey = `product_${idx}_${f.key}`;
        lines.push(`${escapeCsvValue(indexedKey)},${escapeCsvValue(val)}`);
      }
    });
  });

  const csvContent = `key,value\n${lines.join("\n")}`;
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  return new File([blob], "data.csv", { type: "text/csv" });
}

function emptyBusinessValues(): Record<string, string> {
  const initial: Record<string, string> = {};
  BUSINESS_FIELDS_KEYS.forEach((f) => {
    initial[f.key] = "";
  });
  return initial;
}

export default function KnowledgeBasePanel({ csvData, loading, phone, onImport }: KnowledgeBasePanelProps) {
  const { t } = useTranslation("dashboard");
  const BUSINESS_FIELDS = getBusinessFields(t);
  const PRODUCT_FIELDS = getProductFields(t);
  const [businessValues, setBusinessValues] = useState<Record<string, string>>(emptyBusinessValues);
  const [products, setProducts] = useState<ProductEntry[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const [expandedSection, setExpandedSection] = useState<"business" | "product" | null>("business");
  const [importerOpen, setImporterOpen] = useState(false);

  useEffect(() => {
    if (csvData.length > 0) {
      const nextBusiness: Record<string, string> = {};
      BUSINESS_FIELDS.forEach((f) => {
        const match = csvData.find((row) => row.key === f.key);
        nextBusiness[f.key] = match ? match.value : "";
      });
      setBusinessValues(nextBusiness);

      const parsedProducts = parseProductsFromCsv(csvData);
      if (parsedProducts.length > 0) {
        setProducts(parsedProducts);
      }
    }
  }, [csvData]);

  useEffect(() => {
    if (feedback) {
      const timer = setTimeout(() => setFeedback(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  const handleBusinessChange = (key: string, value: string) => {
    setBusinessValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleProductChange = useCallback((productId: string, fieldKey: string, value: string) => {
    setProducts((prev) =>
      prev.map((p) =>
        p.id === productId ? { ...p, values: { ...p.values, [fieldKey]: value } } : p
      )
    );
  }, []);

  const addProduct = () => {
    setProducts((prev) => [...prev, { id: nextProductId(), values: emptyProductValues() }]);
  };

  const removeProduct = (productId: string) => {
    setProducts((prev) => prev.filter((p) => p.id !== productId));
  };

  const handleCsvProductsImport = useCallback((importedProducts: Record<string, string>[]) => {
    const newProducts = importedProducts.map((values) => ({
      id: nextProductId(),
      values: {
        product_name: values.product_name || "",
        product_description: values.product_description || "",
        category: values.category || "",
        brand: values.brand || "",
        price: values.price || "",
        features: values.features || "",
        inventory_status: values.inventory_status || "",
        product_url: values.product_url || "",
      },
    }));
    setProducts((prev) => [...prev, ...newProducts]);
    if (!expandedSection || expandedSection !== "product") {
      setExpandedSection("product");
    }
  }, [expandedSection]);

  const businessFilledCount = BUSINESS_FIELDS.filter((f) => businessValues[f.key]?.trim()).length;
  const productsFilledCount = products.reduce((count, p) => {
    return count + PRODUCT_FIELDS.filter((f) => p.values[f.key]?.trim()).length;
  }, 0);
  const totalFilled = businessFilledCount + productsFilledCount;

  const handleSubmit = async () => {
    if (totalFilled === 0) {
      setFeedback({ type: "error", msg: t("kb.fillRequired") });
      return;
    }

    setSubmitting(true);
    const csvFile = buildKeyValueCsv(businessValues, products);
    const result = await onImport(csvFile, phone);
    setSubmitting(false);

    if (result.success) {
      setFeedback({ type: "success", msg: t("kb.submitSuccess", { fields: totalFilled, sections: products.length + 1, rows: result.data?.rows || 0 }) });
    } else {
      setFeedback({ type: "error", msg: result.message || t("kb.submitFailed") });
    }
  };

  const renderField = (field: FormField, value: string, onChange: (v: string) => void) => {
    if (field.isTextarea) {
      return (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          maxLength={500}
          rows={3}
          className="w-full px-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
        />
      );
    }
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        className="w-full px-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
      />
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg text-foreground-950">{t("kb.title")}</h3>
          <p className="text-foreground-400 text-xs mt-0.5">
            {t("kb.subtitle", { products: products.length, plural: products.length !== 1 ? "s" : "" })}
          </p>
        </div>
        {feedback && (
          <span
            className={`text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap ${
              feedback.type === "success"
                ? "bg-emerald-50 text-emerald-600"
                : "bg-red-50 text-red-600"
            }`}
          >
            {feedback.msg}
          </span>
        )}
      </div>

      {/* Business Information Section */}
      <div className="rounded-2xl border border-background-200/50 bg-background-50 overflow-hidden">
        <div
          className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100/40 transition-colors"
          onClick={() => setExpandedSection(expandedSection === "business" ? null : "business")}
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-background-100 flex items-center justify-center">
              <i className="ri-building-4-line text-foreground-600 text-base"></i>
            </div>
            <div>
              <p className="text-foreground-950 font-semibold text-sm">{t("kb.businessSection")}</p>
              <p className="text-foreground-400 text-xs">{t("kb.businessDesc")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">
              {businessFilledCount}/{BUSINESS_FIELDS.length}
            </span>
            <i className={`ri-arrow-down-s-line text-foreground-400 text-base transition-transform ${expandedSection === "business" ? "rotate-180" : ""}`}></i>
          </div>
        </div>

        {expandedSection === "business" && (
          <div className="px-5 pb-5 border-t border-background-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
              {BUSINESS_FIELDS.map((field) => (
                <div key={field.key} className={field.isTextarea ? "sm:col-span-2" : ""}>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                    {field.label}
                  </label>
                  {renderField(field, businessValues[field.key], (v) => handleBusinessChange(field.key, v))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Product Information Section */}
      <div className="rounded-2xl border border-background-200/50 bg-background-50 overflow-hidden">
        <div
          className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100/40 transition-colors"
          onClick={() => setExpandedSection(expandedSection === "product" ? null : "product")}
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-background-100 flex items-center justify-center">
              <i className="ri-shopping-bag-3-line text-foreground-600 text-base"></i>
            </div>
            <div>
              <p className="text-foreground-950 font-semibold text-sm">{t("kb.productSection")}</p>
              <p className="text-foreground-400 text-xs">
                {products.length > 0
                  ? t("kb.productDescWithCount", { count: products.length, plural: products.length !== 1 ? "s" : "", fields: productsFilledCount })
                  : t("kb.productDesc")}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); setImporterOpen(true); }}
              className="px-3 py-1.5 rounded-lg border border-background-200 text-xs text-foreground-500 hover:text-primary-600 hover:border-primary-300 hover:bg-primary-50/30 transition-all cursor-pointer flex items-center gap-1.5 whitespace-nowrap"
            >
              <i className="ri-file-excel-2-line text-sm"></i>
              {t("kb.importCsv")}
            </button>
            <span className="text-[10px] text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">
              {products.length}
            </span>
            <i className={`ri-arrow-down-s-line text-foreground-400 text-base transition-transform ${expandedSection === "product" ? "rotate-180" : ""}`}></i>
          </div>
        </div>

        {expandedSection === "product" && (
          <div className="px-5 pb-5 border-t border-background-100">
            <div className="pt-4 space-y-4">
              {products.map((product, idx) => {
                const productFilled = PRODUCT_FIELDS.filter((f) => product.values[f.key]?.trim()).length;
                return (
                  <div
                    key={product.id}
                    className="rounded-xl border border-background-200/60 bg-background-50 overflow-hidden"
                  >
                    <div className="flex items-center justify-between px-4 py-3 bg-background-100/50">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-7 h-7 rounded-lg bg-background-200 flex items-center justify-center shrink-0">
                          <i className="ri-shopping-bag-line text-foreground-500 text-xs"></i>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground-900 truncate">
                            {product.values.product_name?.trim() || t("kb.productDefault", { index: idx + 1 })}
                          </p>
                          <p className="text-[10px] text-foreground-400">
                            {t("kb.fieldsCount", { filled: productFilled, total: PRODUCT_FIELDS.length })}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => removeProduct(product.id)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer shrink-0 ml-2"
                        title={t("kb.removeProduct")}
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>

                    <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {PRODUCT_FIELDS.map((field) => (
                        <div key={field.key} className={field.isTextarea ? "sm:col-span-2" : ""}>
                          <label className="block text-[11px] font-medium text-foreground-500 mb-1">
                            {field.label}
                          </label>
                          {renderField(field, product.values[field.key], (v) => handleProductChange(product.id, field.key, v))}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              <button
                onClick={addProduct}
                className="w-full py-3 rounded-xl border-2 border-dashed border-background-200 hover:border-primary-300 hover:bg-primary-50/30 text-foreground-400 hover:text-primary-500 text-sm font-medium transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-add-line text-base"></i>
                {t("kb.addProduct")}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Submit Bar */}
      <div className="flex items-center justify-between bg-background-50 rounded-2xl border border-background-200/50 px-5 py-3.5">
        <div className="flex items-center gap-2 text-xs text-foreground-400">
          <i className="ri-information-line text-sm"></i>
          <span>{t("kb.fieldsFilled", { total: totalFilled, sections: products.length + 1 })}</span>
        </div>
        <button
          onClick={handleSubmit}
          disabled={loading || submitting}
          className="px-8 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
        >
          {submitting ? (
            <span className="flex items-center gap-2">
              <i className="ri-loader-4-line animate-spin text-sm"></i>
              {t("kb.submitting")}
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <i className="ri-send-plane-line text-sm"></i>
              {t("kb.submitCsv")}
            </span>
          )}
        </button>
      </div>

      {/* CSV Product Import Modal */}
      <CsvProductImporter
        isOpen={importerOpen}
        onClose={() => setImporterOpen(false)}
        onImport={handleCsvProductsImport}
        existingProductNames={products.map((p) => p.values.product_name?.trim()).filter(Boolean)}
      />
    </div>
  );
}